import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  TrendingUp,
  Wallet as WalletIcon,
  Users,
  Award,
  BarChart3,
  HeadphonesIcon,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  CreditCard,
  DollarSign,
} from 'lucide-react';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navigation =
    user?.role === 'admin'
      ? [
          { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
          { name: 'Partners', href: '/admin/users', icon: Users },
          { name: 'Production Funds', href: '/admin/deposits', icon: TrendingUp },
          { name: 'Earnings Report', href: '/admin/earnings', icon: DollarSign },
          { name: 'KYC Review', href: '/admin/kyc', icon: Shield },
          { name: 'Production Performance', href: '/admin/performance', icon: BarChart3 },
          { name: 'Bank Accounts', href: '/admin/bank-details', icon: CreditCard },
          { name: 'Profit Settlements', href: '/admin/withdrawals', icon: WalletIcon },
          { name: 'Support', href: '/admin/support', icon: HeadphonesIcon },
          { name: 'Partner Network Settings', href: '/admin/mlm-settings', icon: Users },
          { name: 'System Settings', href: '/admin/settings', icon: Settings },
          { name: 'Audit Logs', href: '/admin/audit-logs', icon: Settings },
        ]
      : [
          { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
          { name: 'Production Plans', href: '/plans', icon: TrendingUp },
          { name: 'Earnings', href: '/wallet', icon: WalletIcon },
          { name: 'Production Performance', href: '/arbitrage', icon: BarChart3 },
          { name: 'KYC Verification', href: '/kyc', icon: Shield },
          { name: 'Start Production Cycle', href: '/deposits', icon: TrendingUp },
        ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* Top Bar */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
              >
                {mobileMenuOpen ? <X /> : <Menu />}
              </button>

              <img
                src="/logo.png"
                alt="Tikhat Partner"
                className="h-10 object-contain"
              />
              <span className="font-semibold text-[#0A4683] hidden sm:block">
                Tikhat Partner
              </span>
            </div>

            <div className="flex items-center gap-4">
              {user?.kycStatus !== 'Approved' && (
                <div className="hidden md:flex items-center gap-2 bg-warning-light border border-warning px-3 py-1 rounded-md">
                  <Shield className="w-4 h-4 text-warning" />
                  <span className="text-sm font-medium text-warning-dark">
                    KYC {user?.kycStatus}
                  </span>
                </div>
              )}

              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>

              <button
                onClick={handleLogout}
                className="p-2 rounded-md text-gray-600 hover:text-error hover:bg-error-light"
                title="Logout"
              >
                <LogOut />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t bg-white">
            <div className="px-2 py-3 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${
                      isActive(item.href)
                        ? 'bg-primary-light text-primary'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:pt-16 bg-white border-r">
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${
                    isActive(item.href)
                      ? 'bg-primary-light text-primary'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 lg:ml-64">
          <div className="max-w-7xl mx-auto px-4 py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
